import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  Handle,
  Position,
  MarkerType,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from '../utils/toast';
import {
  ArrowLeft, Play, FileText, Layers, Pencil, Image, Film, Loader2,
  AlertCircle, Users, Shirt, MapPin, ScanFace, Eye, EyeOff, Scissors,
  X, RefreshCw, ChevronRight, DollarSign, Clock, CheckCircle2,
  Pause, RotateCcw, Expand, ExternalLink, Activity, Sun, Palette,
} from 'lucide-react';

const API = `${window.__API_BASE}/api`;
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

// ─── Status badge colors ───
const STATUS_STYLES = {
  idle:     { bg: 'rgba(19,27,44,0.6)', border: 'rgba(14,165,233,0.15)', dot: '#64748B', label: 'Idle', color: '#64748B' },
  running:  { bg: 'rgba(14,165,233,0.08)', border: 'rgba(14,165,233,0.4)', dot: '#0EA5E9', label: 'Running', color: '#0EA5E9', pulse: true },
  complete: { bg: 'rgba(16,185,129,0.08)', border: 'rgba(16,185,129,0.4)', dot: '#10B981', label: 'Complete', color: '#10B981' },
  error:    { bg: 'rgba(239,68,68,0.08)', border: 'rgba(239,68,68,0.4)', dot: '#EF4444', label: 'Error', color: '#EF4444' },
  waiting:  { bg: 'rgba(245,158,11,0.06)', border: 'rgba(245,158,11,0.3)', dot: '#F59E0B', label: 'Waiting', color: '#F59E0B', pulse: true },
};

// ─── Node definitions ───
const NODE_DEFS = {
  script:      { icon: FileText, label: 'Script Input',       sublabel: 'Upload or paste script' },
  breakdown:   { icon: Layers,   label: 'Shot Breakdown',     sublabel: 'AI scene analysis' },
  asset_bible: { icon: Users,    label: 'Asset Bible',        sublabel: 'Cast, costume, locations' },
  sketches:    { icon: Pencil,   label: 'Sketches',           sublabel: 'B&W storyboard frames' },
  keyframes:   { icon: Image,    label: 'Keyframes',          sublabel: 'Photorealistic frames' },
  face:        { icon: ScanFace, label: 'Face Consistency',   sublabel: 'Face swap & match' },
  vlm_qa:      { icon: Eye,      label: 'VLM QA',             sublabel: 'Quality analysis' },
  video:       { icon: Film,     label: 'Video Generation',   sublabel: 'Image-to-Video · multiple engines' },
  post:        { icon: Scissors, label: 'Post-Production',    sublabel: 'Grading, sound, export' },
};

// ─── Helper: parse progress string like "5/15" ───
function parseProgress(str) {
  if (!str) return 0;
  const parts = str.split('/');
  if (parts.length !== 2) return 0;
  const num = Number(parts[0]), den = Number(parts[1]);
  return den > 0 ? Math.round((num / den) * 100) : 0;
}

// ─── Pipeline Node Component ───
function PipelineNode({ data, selected }) {
  const status = data.status || 'idle';
  const style = STATUS_STYLES[status] || STATUS_STYLES.idle;
  const def = NODE_DEFS[data.nodeType] || {};
  const Icon = def.icon || FileText;
  const canRun = data.canRun && status !== 'running';
  const thumbnails = data.thumbnails || [];

  return (
    <div
      data-testid={`canvas-node-${data.nodeType}`}
      className="relative group"
      style={{ width: 260 }}
    >
      {/* Glow ring for selected */}
      {selected && (
        <div className="absolute -inset-1 rounded-xl opacity-60" 
          style={{ background: 'linear-gradient(135deg, rgba(14,165,233,0.3), rgba(20,184,166,0.2))', filter: 'blur(8px)' }} />
      )}
      
      <div
        className="relative rounded-xl overflow-hidden transition-all duration-300"
        style={{
          background: style.bg,
          border: `1px solid ${style.border}`,
          backdropFilter: 'blur(12px)',
          boxShadow: selected 
            ? '0 0 30px rgba(14,165,233,0.2)' 
            : '0 4px 20px rgba(0,0,0,0.3)',
        }}
      >
        {/* Handles */}
        {data.nodeType !== 'script' && (
          <Handle type="target" position={Position.Left}
            className="!w-3 !h-3 !border-2"
            style={{ background: style.dot, borderColor: '#0B0F19' }} />
        )}
        {data.nodeType !== 'post' && (
          <Handle type="source" position={Position.Right}
            className="!w-3 !h-3 !border-2"
            style={{ background: style.dot, borderColor: '#0B0F19' }} />
        )}

        {/* Header */}
        <div className="flex items-center gap-2.5 px-3 py-2.5" style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
          <div className="p-1.5 rounded-lg" style={{ background: status === 'running' ? 'rgba(14,165,233,0.2)' : 'rgba(255,255,255,0.05)' }}>
            <Icon className="w-3.5 h-3.5" style={{ color: status === 'complete' ? '#10B981' : status === 'running' ? '#0EA5E9' : '#94A3B8' }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-semibold text-white truncate">{def.label}</div>
            <div className="text-[9px] font-mono" style={{ color: '#64748B' }}>{def.sublabel}</div>
          </div>
          <div className="w-2 h-2 rounded-full shrink-0" style={{ 
            background: style.dot,
            animation: style.pulse ? 'pulse 2s ease-in-out infinite' : 'none',
          }} />
        </div>

        {/* Body */}
        <div className="px-3 py-2 space-y-1.5">
          {/* Status row */}
          <div className="flex items-center justify-between">
            <span className="text-[9px] uppercase tracking-wider" style={{ color: '#64748B' }}>Status</span>
            <span className="text-[9px] font-mono font-medium" style={{ color: style.color }}>{style.label}</span>
          </div>

          {/* Progress bar */}
          {status === 'running' && data.progress && (
            <div>
              <div className="flex items-center justify-between mb-0.5">
                <span className="text-[9px]" style={{ color: '#64748B' }}>Progress</span>
                <span className="text-[9px] font-mono" style={{ color: '#0EA5E9' }}>{data.progress}</span>
              </div>
              <div className="h-1 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.05)' }}>
                <div className="h-full rounded-full transition-all duration-700" 
                  style={{ width: `${parseProgress(data.progress)}%`, background: 'linear-gradient(90deg, #0EA5E9, #14B8A6)' }} />
              </div>
            </div>
          )}

          {/* Stats */}
          {data.stats && (
            <div className="flex gap-1.5 pt-0.5">
              {Object.entries(data.stats).map(([k, v]) => (
                <div key={k} className="flex-1 text-center p-1 rounded-md" style={{ background: 'rgba(255,255,255,0.03)' }}>
                  <div className="text-[8px]" style={{ color: '#64748B' }}>{k}</div>
                  <div className="text-[10px] font-mono text-white">{v}</div>
                </div>
              ))}
            </div>
          )}

          {/* Mini thumbnails for completed stages */}
          {status === 'complete' && thumbnails.length > 0 && (
            <div className="flex gap-1 pt-1">
              {thumbnails.slice(0, 4).map((url, i) => (
                <div key={i} className="w-[55px] h-[32px] rounded overflow-hidden" 
                  style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
                  <img src={`${window.__API_BASE}${url}`} alt="" 
                    className="w-full h-full object-cover" 
                    onError={(e) => { e.target.style.display = 'none'; }} />
                </div>
              ))}
              {thumbnails.length > 4 && (
                <div className="w-[55px] h-[32px] rounded flex items-center justify-center text-[8px] font-mono" 
                  style={{ background: 'rgba(255,255,255,0.03)', color: '#64748B' }}>
                  +{thumbnails.length - 4}
                </div>
              )}
            </div>
          )}

          {/* Error */}
          {status === 'error' && data.error && (
            <div className="flex items-start gap-1 p-1.5 rounded-md" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)' }}>
              <AlertCircle className="w-2.5 h-2.5 mt-0.5 shrink-0" style={{ color: '#EF4444' }} />
              <span className="text-[8px] leading-tight" style={{ color: 'rgba(239,68,68,0.8)' }}>{data.error}</span>
            </div>
          )}

          {/* Run button */}
          {canRun && (
            <button
              onClick={(e) => { e.stopPropagation(); data.onRun?.(); }}
              data-testid={`canvas-run-${data.nodeType}`}
              className="w-full flex items-center justify-center gap-1.5 py-1.5 rounded-lg 
                text-[10px] font-semibold transition-all hover:scale-[1.02] active:scale-[0.98]"
              style={{
                background: 'rgba(14,165,233,0.1)',
                border: '1px solid rgba(14,165,233,0.3)',
                color: '#0EA5E9',
              }}
            >
              <Play className="w-3 h-3" /> Run
            </button>
          )}
        </div>
      </div>
    </div>
  );
}


// ─── Rich Side Panel ───
function NodePanel({ node, project, shots, onClose, onRun }) {
  const [activeTab, setActiveTab] = useState('overview');
  
  // SAM segmentation data — all hooks before early return
  const [samData, setSamData] = useState(null);
  const [samLoading, setSamLoading] = useState(false);
  const [samRerunning, setSamRerunning] = useState(false);
  const [samActiveStage, setSamActiveStage] = useState('8c');
  const [samSelectedShot, setSamSelectedShot] = useState(null);
  const [samShowOverlay, setSamShowOverlay] = useState(true);
  const [samHoveredMask, setSamHoveredMask] = useState(null);
  const [samProjectStats, setSamProjectStats] = useState(null);

  const projectId = project?.id;

  useEffect(() => {
    if (activeTab === 'sam' && projectId) {
      axios.get(`${API}/recipes/sam/segmentation/${projectId}`, { headers: getHeaders() })
        .then(r => setSamProjectStats(r.data)).catch(() => {});
    }
  }, [activeTab, projectId]);

  useEffect(() => {
    if (!samSelectedShot && activeTab === 'sam' && shots?.length) {
      const first = shots.find(s => s.keyframe_url);
      if (first) setSamSelectedShot(first.id);
    }
  }, [activeTab, shots, samSelectedShot]);

  useEffect(() => {
    if (samSelectedShot && projectId && activeTab === 'sam') {
      setSamLoading(true);
      axios.get(`${API}/recipes/sam/segmentation/${projectId}/${samSelectedShot}`, { headers: getHeaders() })
        .then(r => setSamData(r.data))
        .catch(() => setSamData(null))
        .finally(() => setSamLoading(false));
    }
  }, [samSelectedShot, projectId, activeTab]);

  if (!node) return null;
  const nodeType = node.data.nodeType;
  const def = NODE_DEFS[nodeType] || {};
  const Icon = def.icon || FileText;
  const status = node.data.status || 'idle';
  const style = STATUS_STYLES[status] || STATUS_STYLES.idle;
  const canRun = node.data.canRun && status !== 'running';
  const hasKeyframes = shots?.some(s => s.keyframe_url);
  const showSamTab = hasKeyframes;

  const LABEL_COLORS = {
    person: '#3B82F6', face: '#EC4899', skin: '#F59E0B', hair: '#8B5CF6',
    clothing: '#06B6D4', sky: '#38BDF8', building: '#F97316', vehicle: '#EF4444',
    ground: '#84CC16', furniture: '#A78BFA', animal: '#FB923C', text: '#FBBF24',
    background: '#475569', wall: '#D4A574', floor: '#92400E', window: '#7DD3FC',
    foliage: '#22C55E', water: '#0EA5E9', metal: '#9CA3AF', hands: '#FCA5A5',
  };
  const getLabelColor = (label) => LABEL_COLORS[label] || `hsl(${(label||'').split('').reduce((a,c)=>a+c.charCodeAt(0),0) % 360}, 70%, 55%)`;

  const samRerunShot = async () => {
    if (!samSelectedShot || !projectId) return;
    setSamRerunning(true);
    try {
      await axios.post(`${API}/recipes/sam/rerun/${projectId}/${samSelectedShot}`, {}, { headers: getHeaders() });
      toast.success('SAM re-segmentation complete');
      const r = await axios.get(`${API}/recipes/sam/segmentation/${projectId}/${samSelectedShot}`, { headers: getHeaders() });
      setSamData(r.data);
      const s = await axios.get(`${API}/recipes/sam/segmentation/${projectId}`, { headers: getHeaders() });
      setSamProjectStats(s.data);
    } catch (e) {
      toast.error(`Re-run failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSamRerunning(false);
    }
  };

  const samRerunAll = async () => {
    if (!projectId) return;
    setSamRerunning(true);
    try {
      await axios.post(`${API}/recipes/sam/rerun/${projectId}`, {}, { headers: getHeaders() });
      toast.success('Full project re-segmentation complete');
      if (samSelectedShot) {
        const r = await axios.get(`${API}/recipes/sam/segmentation/${projectId}/${samSelectedShot}`, { headers: getHeaders() });
        setSamData(r.data);
      }
      const s = await axios.get(`${API}/recipes/sam/segmentation/${projectId}`, { headers: getHeaders() });
      setSamProjectStats(s.data);
    } catch (e) {
      toast.error(`Re-run failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSamRerunning(false);
    }
  };

  const activeMasks = samData?.raw_stages?.find(s => s.stage === samActiveStage)?.masks || [];
  const activeSummary = samData?.raw_stages?.find(s => s.stage === samActiveStage)?.summary || '';
  const frameW = samData?.raw_stages?.find(s => s.stage === samActiveStage)?.frame_width || 1;
  const frameH = samData?.raw_stages?.find(s => s.stage === samActiveStage)?.frame_height || 1;
  const shotsWithKf = shots?.filter(s => s.keyframe_url) || [];
  const currentShotObj = shotsWithKf.find(s => s.id === samSelectedShot);

  const getSamContent = () => (
    <div className="space-y-3" data-testid="sam-panel">
      {/* Project stats */}
      {samProjectStats && (
        <div className="flex gap-1.5 flex-wrap" data-testid="sam-project-stats">
          {[['8c','Comp'], ['11','Light'], ['12','Color']].map(([key, label]) => (
            <div key={key} className="flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-mono"
              style={{ background: 'rgba(13,148,136,0.1)', border: '1px solid rgba(13,148,136,0.2)', color: '#94A3B8' }}>
              <span>{label}</span>
              <span style={{ color: '#0D9488', fontWeight: 700 }}>{samProjectStats.stats?.[key] || 0}</span>
            </div>
          ))}
        </div>
      )}

      {/* Shot selector */}
      <div className="flex gap-1.5 overflow-x-auto pb-1" style={{ scrollbarWidth: 'thin' }}>
        {shotsWithKf.map(shot => (
          <button key={shot.id}
            className="shrink-0 flex flex-col items-center gap-1 p-1 rounded-lg transition-all"
            style={{
              background: shot.id === samSelectedShot ? 'rgba(13,148,136,0.15)' : '#131B2C',
              border: shot.id === samSelectedShot ? '1.5px solid #0D9488' : '1.5px solid #1E293B',
            }}
            onClick={() => setSamSelectedShot(shot.id)}
            data-testid={`sam-shot-${shot.scene_number}-${shot.shot_number}`}>
            <img src={shot.keyframe_url?.startsWith('http') ? shot.keyframe_url : `${window.__API_BASE}${shot.keyframe_url}`} alt=""
              className="w-12 h-9 object-cover rounded" onError={e => { e.target.style.display = 'none'; }} />
            <span className="text-[9px] font-mono" style={{ color: shot.id === samSelectedShot ? '#0D9488' : '#64748B' }}>
              {shot.scene_number}/{shot.shot_number}
            </span>
          </button>
        ))}
      </div>

      {samLoading ? (
        <div className="flex items-center justify-center gap-2 py-6" style={{ color: '#64748B' }}>
          <Loader2 className="w-4 h-4 animate-spin" /><span className="text-xs">Loading...</span>
        </div>
      ) : currentShotObj ? (
        <>
          {/* Stage toggle */}
          <div className="flex gap-1">
            {[['8c','Composition','Layers'],['11','Lighting','Sun'],['12','Color','Palette']].map(([key, label]) => {
              const hasData = samData?.raw_stages?.some(s => s.stage === key);
              return (
                <button key={key}
                  className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-md text-[10px] font-medium transition-all"
                  style={{
                    background: samActiveStage === key ? 'rgba(13,148,136,0.12)' : '#0F172A',
                    border: samActiveStage === key ? '1px solid #0D9488' : '1px solid #1E293B',
                    color: samActiveStage === key ? '#0D9488' : '#64748B',
                    opacity: hasData ? 1 : 0.5,
                  }}
                  onClick={() => setSamActiveStage(key)}
                  data-testid={`sam-stage-${key}`}>
                  {label}
                  {hasData && <span className="w-1.5 h-1.5 rounded-full" style={{ background: '#10B981' }} />}
                </button>
              );
            })}
          </div>

          {/* Keyframe with overlay */}
          <div className="relative rounded-lg overflow-hidden" style={{ border: '1px solid #1E293B' }}
            data-testid="sam-preview">
            <img src={currentShotObj.keyframe_url?.startsWith('http') ? currentShotObj.keyframe_url : `${window.__API_BASE}${currentShotObj.keyframe_url}`} alt="Keyframe"
              className="w-full block" data-testid="sam-keyframe-image" />

            {samShowOverlay && activeMasks.length > 0 && (
              <svg className="absolute inset-0 w-full h-full" viewBox={`0 0 ${frameW} ${frameH}`}
                preserveAspectRatio="none" style={{ pointerEvents: 'none' }} data-testid="sam-overlay">
                {activeMasks.map((mask, i) => {
                  const color = getLabelColor(mask.label);
                  const [x, y, w, h] = mask.box || [0,0,0,0];
                  const rx = x - w/2; const ry = y - h/2;
                  const hovered = samHoveredMask === i;
                  return (
                    <g key={i} style={{ pointerEvents: 'all', cursor: 'crosshair' }}
                      onMouseEnter={() => setSamHoveredMask(i)} onMouseLeave={() => setSamHoveredMask(null)}>
                      <rect x={rx} y={ry} width={w} height={h}
                        fill={hovered ? `${color}40` : `${color}20`}
                        stroke={color} strokeWidth={hovered ? 3 : 1.5} rx={2} />
                      <text x={rx+4} y={ry+14} fill={color} fontSize={12} fontWeight="600"
                        fontFamily="JetBrains Mono, monospace">
                        {mask.label} {Math.round(mask.confidence * 100)}%
                      </text>
                    </g>
                  );
                })}
              </svg>
            )}

            <button className="absolute top-1.5 right-1.5 flex items-center gap-1 px-2 py-1 rounded-md text-[9px] font-mono"
              style={{ background: 'rgba(11,15,25,0.85)', backdropFilter: 'blur(8px)', border: '1px solid #1E293B', color: '#94A3B8' }}
              onClick={() => setSamShowOverlay(!samShowOverlay)} data-testid="sam-overlay-toggle">
              {samShowOverlay ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
              {samShowOverlay ? 'Hide' : 'Show'}
            </button>
          </div>

          {/* Summary */}
          {activeSummary && (
            <div className="p-2 rounded-md" style={{ background: '#0F172A', border: '1px solid #1E293B' }}
              data-testid="sam-summary">
              <p className="text-[10px] font-mono leading-relaxed break-words" style={{ color: '#94A3B8' }}>
                {activeSummary}
              </p>
            </div>
          )}

          {/* Legend */}
          {activeMasks.length > 0 && (
            <div className="space-y-0.5" data-testid="sam-legend">
              {activeMasks.map((mask, i) => {
                const color = getLabelColor(mask.label);
                const [,,w,h] = mask.box || [0,0,0,0];
                const pct = frameW*frameH > 0 ? ((w*h)/(frameW*frameH)*100).toFixed(1) : 0;
                return (
                  <div key={i} className="flex items-center gap-1.5 px-2 py-1 rounded transition-colors"
                    style={{ background: samHoveredMask === i ? 'rgba(255,255,255,0.04)' : 'transparent' }}
                    onMouseEnter={() => setSamHoveredMask(i)} onMouseLeave={() => setSamHoveredMask(null)}>
                    <span className="w-2 h-2 rounded-sm shrink-0" style={{ background: color }} />
                    <span className="flex-1 text-[10px] font-medium capitalize" style={{ color: '#CBD5E1' }}>{mask.label}</span>
                    <span className="text-[9px] font-mono" style={{ color: '#64748B' }}>{Math.round(mask.confidence*100)}%</span>
                    <span className="text-[9px] font-mono w-8 text-right" style={{ color: '#475569' }}>{pct}%</span>
                  </div>
                );
              })}
            </div>
          )}

          {activeMasks.length === 0 && !samLoading && (
            <div className="text-center py-4 text-[11px]" style={{ color: '#475569' }}>
              No segmentation data. Click Re-run SAM to generate.
            </div>
          )}

          {/* Re-run buttons */}
          <div className="space-y-1.5" data-testid="sam-actions">
            <button onClick={samRerunShot} disabled={samRerunning}
              className="w-full flex items-center justify-center gap-1.5 py-2 rounded-md text-[11px] font-semibold transition-all"
              style={{ background: 'rgba(13,148,136,0.12)', border: '1px solid rgba(13,148,136,0.3)', color: '#0D9488',
                opacity: samRerunning ? 0.5 : 1, cursor: samRerunning ? 'not-allowed' : 'pointer' }}
              data-testid="sam-rerun-shot">
              {samRerunning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              Re-run SAM (This Shot)
            </button>
            <button onClick={samRerunAll} disabled={samRerunning}
              className="w-full flex items-center justify-center gap-1.5 py-2 rounded-md text-[11px] font-semibold transition-all"
              style={{ background: 'rgba(100,116,139,0.1)', border: '1px solid rgba(100,116,139,0.2)', color: '#94A3B8',
                opacity: samRerunning ? 0.5 : 1, cursor: samRerunning ? 'not-allowed' : 'pointer' }}
              data-testid="sam-rerun-project">
              {samRerunning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              Re-run All Shots
            </button>
          </div>
        </>
      ) : null}
    </div>
  );

  // Get stage-specific content
  const getContent = () => {
    switch (nodeType) {
      case 'script':
        return (
          <div className="space-y-3">
            <SectionHeader title="Script Preview" />
            {project?.script_text ? (
              <pre className="text-[11px] leading-relaxed whitespace-pre-wrap max-h-[300px] overflow-y-auto p-3 rounded-lg font-mono"
                style={{ background: 'rgba(0,0,0,0.3)', color: '#94A3B8' }}>
                {project.script_text.substring(0, 1500)}{project.script_text.length > 1500 ? '\n\n...' : ''}
              </pre>
            ) : (
              <EmptyState text="No script uploaded yet" />
            )}
            {project?.entities && project.entities.length > 0 && (
              <>
                <SectionHeader title={`Entities (${project.entities.length})`} />
                <div className="flex flex-wrap gap-1.5">
                  {project.entities.slice(0, 12).map((e, i) => (
                    <span key={i} className="text-[10px] px-2 py-0.5 rounded-full font-mono"
                      style={{ background: 'rgba(14,165,233,0.1)', border: '1px solid rgba(14,165,233,0.2)', color: '#0EA5E9' }}>
                      {typeof e === 'string' ? e.split('(')[0].trim() : e}
                    </span>
                  ))}
                </div>
              </>
            )}
          </div>
        );

      case 'breakdown':
        return (
          <div className="space-y-3">
            <SectionHeader title={`Shots (${shots?.length || 0})`} />
            {shots && shots.length > 0 ? (
              <div className="space-y-1.5 max-h-[400px] overflow-y-auto pr-1 custom-scrollbar">
                {shots.map((s, i) => (
                  <div key={i} className="flex items-start gap-2 p-2 rounded-lg transition-colors hover:bg-white/5"
                    style={{ background: 'rgba(0,0,0,0.2)' }}>
                    <span className="text-[9px] font-mono px-1.5 py-0.5 rounded shrink-0"
                      style={{ background: 'rgba(14,165,233,0.15)', color: '#0EA5E9' }}>
                      {s.scene_number}/{s.shot_number}
                    </span>
                    <p className="text-[10px] leading-relaxed line-clamp-2" style={{ color: '#94A3B8' }}>
                      {s.description}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState text="No shots generated yet" />
            )}
          </div>
        );

      case 'asset_bible':
        return (
          <div className="space-y-3">
            <SectionHeader title="Asset Bible" />
            <div className="space-y-2">
              <AssetCategory icon={Users} label="Cast" count={project?.assets_total || '—'} />
              <AssetCategory icon={Shirt} label="Costumes" count="—" />
              <AssetCategory icon={MapPin} label="Locations" count="—" />
            </div>
            {project?.assets_completed && (
              <StatBadge label="Generated" value={`${project.assets_completed}/${project.assets_total || '?'}`} />
            )}
          </div>
        );

      case 'sketches':
        return (
          <div className="space-y-3">
            <SectionHeader title={`Sketches (${project?.sketches_completed || 0}/${project?.sketches_total || shots?.length || 0})`} />
            {shots && shots.some(s => s.sketch_url) ? (
              <div className="grid grid-cols-2 gap-1.5 max-h-[400px] overflow-y-auto pr-1 custom-scrollbar">
                {shots.filter(s => s.sketch_url).map((s, i) => (
                  <div key={i} className="relative group rounded-lg overflow-hidden" 
                    style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
                    <img src={`${window.__API_BASE}${s.sketch_url}`} alt={`${s.scene_number}/${s.shot_number}`}
                      className="w-full aspect-video object-cover" />
                    <div className="absolute bottom-0 left-0 right-0 px-1.5 py-0.5 text-[8px] font-mono"
                      style={{ background: 'linear-gradient(transparent, rgba(0,0,0,0.8))', color: '#94A3B8' }}>
                      {s.scene_number}/{s.shot_number}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState text="No sketches generated yet" />
            )}
          </div>
        );

      case 'keyframes':
        return (
          <div className="space-y-3">
            <SectionHeader title={`Keyframes (${shots?.filter(s => s.keyframe_url).length || 0}/${shots?.length || 0})`} />
            {shots && shots.some(s => s.keyframe_url) ? (
              <div className="grid grid-cols-2 gap-1.5 max-h-[400px] overflow-y-auto pr-1 custom-scrollbar">
                {shots.filter(s => s.keyframe_url).map((s, i) => (
                  <div key={i} className="relative group rounded-lg overflow-hidden"
                    style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
                    <img src={`${window.__API_BASE}${s.keyframe_url}`} alt={`${s.scene_number}/${s.shot_number}`}
                      className="w-full aspect-video object-cover" />
                    <div className="absolute bottom-0 left-0 right-0 px-1.5 py-0.5 text-[8px] font-mono"
                      style={{ background: 'linear-gradient(transparent, rgba(0,0,0,0.8))', color: '#94A3B8' }}>
                      {s.scene_number}/{s.shot_number}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState text="No keyframes generated yet" />
            )}
          </div>
        );

      case 'video':
        return (
          <div className="space-y-3">
            <SectionHeader title={`Videos (${shots?.filter(s => s.video_url || s.video_status === 'completed').length || 0}/${shots?.length || 0})`} />
            {shots && shots.some(s => s.video_url || s.video_status) ? (
              <div className="space-y-1.5 max-h-[400px] overflow-y-auto pr-1 custom-scrollbar">
                {shots.map((s, i) => {
                  const vStatus = s.video_status || 'pending';
                  const vColor = vStatus === 'completed' ? '#10B981' : vStatus === 'processing' ? '#0EA5E9' : vStatus === 'failed' ? '#EF4444' : '#64748B';
                  return (
                    <div key={i} className="flex items-center gap-2 p-2 rounded-lg" style={{ background: 'rgba(0,0,0,0.2)' }}>
                      {s.keyframe_url && (
                        <img src={`${window.__API_BASE}${s.keyframe_url}`} alt="" 
                          className="w-10 h-7 rounded object-cover shrink-0" style={{ border: '1px solid rgba(255,255,255,0.08)' }} />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-[10px] font-mono text-white">{s.scene_number}/{s.shot_number}</div>
                        <div className="text-[9px] font-mono" style={{ color: vColor }}>{vStatus}</div>
                      </div>
                      {vStatus === 'completed' && <CheckCircle2 className="w-3 h-3 shrink-0" style={{ color: '#10B981' }} />}
                      {vStatus === 'processing' && <Loader2 className="w-3 h-3 animate-spin shrink-0" style={{ color: '#0EA5E9' }} />}
                    </div>
                  );
                })}
              </div>
            ) : (
              <EmptyState text="No video generation started" />
            )}
          </div>
        );

      case 'face':
        return (
          <div className="space-y-3">
            <SectionHeader title="Face Consistency" />
            <p className="text-[11px] leading-relaxed" style={{ color: '#94A3B8' }}>
              Manual per-shot face swapping workflow using InsightFace. 
              Requires keyframes to be completed first.
            </p>
            <div className="p-2 rounded-lg text-[10px]" style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.2)', color: '#F59E0B' }}>
              Open in Project View for detailed face swap controls
            </div>
          </div>
        );

      case 'vlm_qa':
        return (
          <div className="space-y-3">
            <SectionHeader title="Visual QA Analysis" />
            {shots && shots.some(s => s.vlm_qa_score != null) ? (
              <div className="space-y-1.5 max-h-[400px] overflow-y-auto pr-1 custom-scrollbar">
                {shots.filter(s => s.vlm_qa_score != null).map((s, i) => {
                  const score = s.vlm_qa_score || 0;
                  const scoreColor = score >= 80 ? '#10B981' : score >= 60 ? '#F59E0B' : '#EF4444';
                  return (
                    <div key={i} className="flex items-center gap-2 p-2 rounded-lg" style={{ background: 'rgba(0,0,0,0.2)' }}>
                      <span className="text-[9px] font-mono px-1.5 py-0.5 rounded shrink-0"
                        style={{ background: 'rgba(14,165,233,0.15)', color: '#0EA5E9' }}>
                        {s.scene_number}/{s.shot_number}
                      </span>
                      <div className="flex-1 h-1 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.05)' }}>
                        <div className="h-full rounded-full" style={{ width: `${score}%`, background: scoreColor }} />
                      </div>
                      <span className="text-[10px] font-mono font-medium" style={{ color: scoreColor }}>{score}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <EmptyState text="No QA analysis run yet" />
            )}
          </div>
        );

      case 'post':
        return (
          <div className="space-y-3">
            <SectionHeader title="Post-Production" />
            <p className="text-[11px] leading-relaxed" style={{ color: '#94A3B8' }}>
              Color grading, sound design, and final export. 
              Requires all videos to be generated first.
            </p>
          </div>
        );

      default:
        return <EmptyState text="No details available" />;
    }
  };

  // Cost summary
  const costData = project?.cost_tracker?.breakdown;
  const totalCost = costData ? Object.values(costData).reduce((sum, c) => sum + (c.total || 0), 0) : 0;

  return (
    <div className="absolute right-0 top-0 h-full w-[340px] flex flex-col z-50"
      data-testid="canvas-node-panel"
      style={{
        background: 'rgba(15,23,42,0.97)',
        borderLeft: '1px solid rgba(14,165,233,0.15)',
        backdropFilter: 'blur(20px)',
        boxShadow: '-8px 0 40px rgba(0,0,0,0.5)',
      }}>
      
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <div className="p-2 rounded-lg" style={{ background: 'rgba(14,165,233,0.1)' }}>
          <Icon className="w-4 h-4" style={{ color: '#0EA5E9' }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-white">{def.label}</div>
          <div className="text-[10px] font-mono" style={{ color: '#64748B' }}>{def.sublabel}</div>
        </div>
        <button onClick={onClose} className="p-1.5 rounded-lg transition-colors" 
          style={{ background: 'rgba(255,255,255,0.05)' }}
          onMouseEnter={(e) => e.target.style.background = 'rgba(255,255,255,0.1)'}
          onMouseLeave={(e) => e.target.style.background = 'rgba(255,255,255,0.05)'}>
          <X className="w-4 h-4" style={{ color: '#64748B' }} />
        </button>
      </div>

      {/* Status bar */}
      <div className="flex items-center gap-3 px-4 py-2.5 shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full" style={{ 
            background: style.dot, 
            animation: style.pulse ? 'pulse 2s ease-in-out infinite' : 'none',
          }} />
          <span className="text-xs font-mono font-medium" style={{ color: style.color }}>{style.label}</span>
        </div>
        {node.data.stats && (
          <div className="flex-1 flex justify-end gap-2">
            {Object.entries(node.data.stats).map(([k, v]) => (
              <div key={k} className="text-center px-2 py-0.5 rounded" style={{ background: 'rgba(255,255,255,0.03)' }}>
                <span className="text-[9px] font-mono" style={{ color: '#64748B' }}>{k}: </span>
                <span className="text-[10px] font-mono text-white">{v}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex px-4 pt-2 gap-1 shrink-0">
        {['overview', ...(showSamTab ? ['sam'] : []), 'config'].map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            className="px-3 py-1.5 text-[10px] font-mono uppercase tracking-wider rounded-md transition-all"
            style={{
              background: activeTab === tab ? (tab === 'sam' ? 'rgba(13,148,136,0.15)' : 'rgba(14,165,233,0.15)') : 'transparent',
              color: activeTab === tab ? (tab === 'sam' ? '#0D9488' : '#0EA5E9') : '#64748B',
              border: activeTab === tab ? `1px solid ${tab === 'sam' ? 'rgba(13,148,136,0.3)' : 'rgba(14,165,233,0.3)'}` : '1px solid transparent',
            }}
            data-testid={`canvas-tab-${tab}`}>
            {tab}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-4 py-3 custom-scrollbar">
        {activeTab === 'overview' ? getContent() : activeTab === 'sam' ? getSamContent() : (
          <div className="space-y-3">
            <SectionHeader title="Pipeline Configuration" />
            <ConfigList items={getConfigItems(nodeType)} />
            {totalCost > 0 && nodeType !== 'script' && (
              <>
                <SectionHeader title="Cost Tracker" />
                <div className="flex items-center gap-2 p-2 rounded-lg" style={{ background: 'rgba(0,0,0,0.2)' }}>
                  <DollarSign className="w-3.5 h-3.5" style={{ color: '#10B981' }} />
                  <span className="text-xs font-mono" style={{ color: '#94A3B8' }}>Total spend:</span>
                  <span className="text-xs font-mono font-medium" style={{ color: '#10B981' }}>${totalCost.toFixed(2)}</span>
                </div>
                {costData && (
                  <div className="space-y-1">
                    {Object.entries(costData).filter(([,v]) => v.total > 0).map(([k, v]) => (
                      <div key={k} className="flex items-center justify-between text-[10px] font-mono px-1">
                        <span style={{ color: '#64748B' }}>{k.replace(/_/g, ' ')}</span>
                        <span style={{ color: '#94A3B8' }}>${v.total.toFixed(2)} ({v.count}x)</span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Run button */}
      {canRun && (
        <div className="px-4 py-3 shrink-0" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <button onClick={() => onRun(nodeType)}
            data-testid={`panel-run-${nodeType}`}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg font-semibold text-sm transition-all hover:opacity-90 active:scale-[0.98]"
            style={{ background: '#0EA5E9', color: '#0B0F19' }}>
            <Play className="w-4 h-4" /> Run {def.label}
          </button>
        </div>
      )}

      {/* Open in project view link */}
      <div className="px-4 py-2 shrink-0" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <a href={`/project/${project?.id}`} target="_blank" rel="noopener noreferrer"
          className="flex items-center justify-center gap-1.5 text-[10px] font-mono transition-colors"
          style={{ color: '#64748B' }}
          onMouseEnter={(e) => e.target.style.color = '#0EA5E9'}
          onMouseLeave={(e) => e.target.style.color = '#64748B'}>
          <ExternalLink className="w-3 h-3" /> Open in Project View
        </a>
      </div>
    </div>
  );
}

// ─── Small UI pieces ───
function SectionHeader({ title }) {
  return (
    <div className="text-[10px] uppercase tracking-wider font-mono font-medium" style={{ color: '#64748B' }}>
      {title}
    </div>
  );
}

function EmptyState({ text }) {
  return (
    <div className="flex items-center justify-center py-6 text-[11px] font-mono" style={{ color: '#475569' }}>
      {text}
    </div>
  );
}

function AssetCategory({ icon: Icon, label, count }) {
  return (
    <div className="flex items-center gap-2.5 p-2 rounded-lg" style={{ background: 'rgba(0,0,0,0.2)' }}>
      <Icon className="w-3.5 h-3.5" style={{ color: '#0EA5E9' }} />
      <span className="text-[11px] flex-1" style={{ color: '#94A3B8' }}>{label}</span>
      <span className="text-[10px] font-mono" style={{ color: '#64748B' }}>{count}</span>
    </div>
  );
}

function StatBadge({ label, value }) {
  return (
    <div className="flex items-center gap-2 text-[10px] font-mono px-2 py-1 rounded" style={{ background: 'rgba(14,165,233,0.08)' }}>
      <span style={{ color: '#64748B' }}>{label}:</span>
      <span style={{ color: '#0EA5E9' }}>{value}</span>
    </div>
  );
}

function ConfigList({ items }) {
  return (
    <div className="space-y-1.5">
      {items.map((item, i) => (
        <div key={i} className="flex items-start gap-2 text-[11px]">
          <ChevronRight className="w-3 h-3 mt-0.5 shrink-0" style={{ color: 'rgba(14,165,233,0.5)' }} />
          <span style={{ color: '#94A3B8' }}>{item}</span>
        </div>
      ))}
    </div>
  );
}

function getConfigItems(nodeType) {
  const configs = {
    script:      ['Script text or file upload', 'AI enhancement available', 'Speech-to-text supported'],
    breakdown:   ['GPT-powered scene analysis', 'Parallel batch processing', 'Best practices v2.1 injected'],
    asset_bible: ['Cast mugshots + refinement', 'Costume reference images', 'Location scouting images', '2-key round-robin'],
    sketches:    ['B&W pencil-style sketches', 'AI image generation', 'Batch size: 4 parallel', 'Iteration & undo supported'],
    keyframes:   ['Photorealistic frames', 'Sketch-to-keyframe pipeline', 'RAW/Grade variants', '4-grid optional'],
    face:        ['InsightFace swap engine', 'Per-shot face matching', 'Manual review workflow'],
    vlm_qa:      ['AI vision analysis', 'Asset consistency check', 'Auto-score 0-100'],
    video:       ['Veo (default)', 'Kling fallback', 'Seedance fallback', '2-key round-robin'],
    post:        ['Color grading presets', 'Sound design integration', 'Timeline export'],
  };
  return configs[nodeType] || [];
}


// ─── Build nodes from project state ───
function buildNodes(project, shots, onRunNode) {
  const stage = project?.current_stage || 0;
  const totalShots = shots?.length || 0;
  const sketchesDone = shots?.filter(s => s.sketch_url)?.length || 0;
  const keyframesDone = shots?.filter(s => s.keyframe_url)?.length || 0;
  const videosDone = shots?.filter(s => s.video_url || s.video_status === 'completed')?.length || 0;

  const stageStatus = (completeAt, runAt) => {
    if (stage >= completeAt) return 'complete';
    if (stage >= runAt) return 'running';
    return 'idle';
  };

  const X_GAP = 320;
  const Y_MAIN = 240;
  const Y_BRANCH_TOP = 60;
  const Y_BRANCH_BOT = Y_MAIN + 180;

  // Collect thumbnails per stage
  const sketchThumbs = shots?.filter(s => s.sketch_url).map(s => s.sketch_url) || [];
  const kfThumbs = shots?.filter(s => s.keyframe_url).map(s => s.keyframe_url) || [];

  return [
    {
      id: 'script', type: 'pipeline', position: { x: 50, y: Y_MAIN },
      data: {
        nodeType: 'script', status: stage >= 1 ? 'complete' : 'idle',
        stats: project?.script_text ? { Chars: String(project.script_text.length) } : null,
      },
    },
    {
      id: 'breakdown', type: 'pipeline', position: { x: 50 + X_GAP, y: Y_MAIN },
      data: {
        nodeType: 'breakdown', status: stageStatus(3, 2),
        canRun: stage >= 1 && stage < 3, onRun: () => onRunNode('breakdown'),
        stats: totalShots > 0 ? { Shots: String(totalShots) } : null,
        progress: project?.shots_progress || null,
      },
    },
    {
      id: 'asset_bible', type: 'pipeline', position: { x: 50 + X_GAP * 2, y: Y_BRANCH_TOP },
      data: {
        nodeType: 'asset_bible', status: stageStatus(6, 4),
        canRun: stage >= 3 && stage < 6, onRun: () => onRunNode('asset_bible'),
        stats: project?.assets_completed ? { Done: `${project.assets_completed}/${project.assets_total || '?'}` } : null,
      },
    },
    {
      id: 'sketches', type: 'pipeline', position: { x: 50 + X_GAP * 2, y: Y_MAIN },
      data: {
        nodeType: 'sketches', status: stageStatus(5, 4),
        canRun: stage >= 3 && stage < 5, onRun: () => onRunNode('sketches'),
        stats: totalShots > 0 ? { Done: `${sketchesDone}/${totalShots}` } : null,
        thumbnails: sketchThumbs,
      },
    },
    {
      id: 'keyframes', type: 'pipeline', position: { x: 50 + X_GAP * 3, y: Y_MAIN },
      data: {
        nodeType: 'keyframes', status: stageStatus(8, 7),
        canRun: stage >= 5 && stage < 8, onRun: () => onRunNode('keyframes'),
        stats: totalShots > 0 ? { Done: `${keyframesDone}/${totalShots}` } : null,
        thumbnails: kfThumbs,
      },
    },
    {
      id: 'face', type: 'pipeline', position: { x: 50 + X_GAP * 4, y: Y_BRANCH_TOP },
      data: {
        nodeType: 'face', status: stageStatus(10, 9),
        canRun: stage >= 8 && stage < 10, onRun: () => onRunNode('face'),
      },
    },
    {
      id: 'vlm_qa', type: 'pipeline', position: { x: 50 + X_GAP * 4, y: Y_BRANCH_BOT },
      data: {
        nodeType: 'vlm_qa', status: stageStatus(11, 10),
        canRun: stage >= 8 && stage < 11, onRun: () => onRunNode('vlm_qa'),
      },
    },
    {
      id: 'video', type: 'pipeline', position: { x: 50 + X_GAP * 5, y: Y_MAIN },
      data: {
        nodeType: 'video', status: stageStatus(16, 14),
        canRun: stage >= 8 && stage < 16, onRun: () => onRunNode('video'),
        stats: totalShots > 0 ? { Done: `${videosDone}/${totalShots}` } : null,
      },
    },
    {
      id: 'post', type: 'pipeline', position: { x: 50 + X_GAP * 6, y: Y_MAIN },
      data: {
        nodeType: 'post', status: stageStatus(17, 16),
        canRun: stage >= 16, onRun: () => onRunNode('post'),
      },
    },
  ];
}

function buildEdges(nodes) {
  const connections = [
    ['script', 'breakdown'],
    ['breakdown', 'asset_bible'],
    ['breakdown', 'sketches'],
    ['sketches', 'keyframes'],
    ['keyframes', 'face'],
    ['keyframes', 'vlm_qa'],
    ['keyframes', 'video'],
    ['face', 'video'],
    ['video', 'post'],
  ];

  return connections.map(([src, tgt]) => {
    const srcNode = nodes.find(n => n.id === src);
    const tgtNode = nodes.find(n => n.id === tgt);
    const srcDone = srcNode?.data?.status === 'complete';
    const tgtDone = tgtNode?.data?.status === 'complete';
    const tgtRunning = tgtNode?.data?.status === 'running';

    let strokeColor = '#1E293B';
    if (srcDone && tgtDone) strokeColor = '#10B981';
    else if (tgtRunning) strokeColor = '#0EA5E9';
    else if (srcDone) strokeColor = '#334155';

    return {
      id: `e-${src}-${tgt}`, source: src, target: tgt,
      type: 'smoothstep',
      animated: srcDone && tgtRunning,
      style: { stroke: strokeColor, strokeWidth: 2, opacity: tgtNode?.data?.status === 'idle' && !srcDone ? 0.15 : 1 },
      markerEnd: { type: MarkerType.ArrowClosed, color: strokeColor, width: 14, height: 14 },
    };
  });
}


// ─── Canvas Page ───
export default function CanvasPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [shots, setShots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedNode, setSelectedNode] = useState(null);
  const pollRef = useRef(null);

  const nodeTypes = useMemo(() => ({ pipeline: PipelineNode }), []);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  // Run a pipeline node
  const onRunNode = useCallback(async (nodeType) => {
    const endpointMap = {
      breakdown: `/projects/${projectId}/generate-shots`,
      asset_bible: `/projects/${projectId}/generate-asset-bible`,
      sketches: `/projects/${projectId}/generate-sketches`,
      keyframes: `/projects/${projectId}/generate-all-keyframes`,
      face: null,
      vlm_qa: `/projects/${projectId}/vlm-qa`,
      video: `/projects/${projectId}/generate-all-videos`,
      post: null,
    };
    const endpoint = endpointMap[nodeType];
    if (!endpoint) {
      toast.info(`${NODE_DEFS[nodeType]?.label} requires manual setup — open in Project View`);
      return;
    }
    try {
      // Update node to running state optimistically
      setNodes(nds => nds.map(n => n.id === nodeType ? { ...n, data: { ...n.data, status: 'running' } } : n));
      await axios.post(`${API}${endpoint}`, {}, { headers: getHeaders() });
      toast.success(`${NODE_DEFS[nodeType]?.label} started`);
    } catch (e) {
      setNodes(nds => nds.map(n => n.id === nodeType ? { ...n, data: { ...n.data, status: 'error', error: e.response?.data?.detail || e.message } } : n));
      toast.error(`Failed: ${e.response?.data?.detail || e.message}`);
    }
  }, [projectId, setNodes]);

  // Load & poll project data
  const loadData = useCallback(async () => {
    try {
      const [projRes, shotsRes] = await Promise.all([
        axios.get(`${API}/projects/${projectId}`, { headers: getHeaders() }),
        axios.get(`${API}/projects/${projectId}/shots`, { headers: getHeaders() }),
      ]);
      const proj = projRes.data;
      const shotsList = shotsRes.data || [];
      setProject(proj);
      setShots(shotsList);
      const newNodes = buildNodes(proj, shotsList, onRunNode);
      setNodes(newNodes);
      setEdges(buildEdges(newNodes));

      // Keep selected node data in sync
      setSelectedNode(prev => {
        if (!prev) return null;
        const updated = newNodes.find(n => n.id === prev.id);
        return updated ? { ...prev, data: updated.data } : prev;
      });
    } catch (e) {
      console.error('Canvas load failed:', e);
    }
  }, [projectId, onRunNode, setNodes, setEdges]);

  useEffect(() => {
    loadData().then(() => setLoading(false));
    pollRef.current = setInterval(loadData, 5000);
    return () => clearInterval(pollRef.current);
  }, [loadData]);

  const onNodeClick = useCallback((_, node) => {
    setSelectedNode(node);
  }, []);

  // Stage progress for top bar
  const completedNodes = nodes.filter(n => n.data?.status === 'complete').length;
  const totalNodes = nodes.length;

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center" style={{ background: '#0B0F19' }}>
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#0EA5E9' }} />
          <span className="text-xs font-mono" style={{ color: '#64748B' }}>Loading canvas...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col" data-testid="canvas-page" style={{ background: '#0B0F19' }}>
      {/* Top bar */}
      <div className="h-12 flex items-center px-4 gap-3 shrink-0"
        style={{ background: 'rgba(15,23,42,0.95)', borderBottom: '1px solid rgba(14,165,233,0.12)', backdropFilter: 'blur(12px)' }}>
        <button onClick={() => navigate(`/project/${projectId}`)}
          data-testid="canvas-back-btn"
          className="flex items-center gap-1.5 text-xs transition-colors"
          style={{ color: '#94A3B8' }}
          onMouseEnter={(e) => e.currentTarget.style.color = '#fff'}
          onMouseLeave={(e) => e.currentTarget.style.color = '#94A3B8'}>
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </button>
        
        <div className="w-px h-5" style={{ background: 'rgba(14,165,233,0.15)' }} />
        
        <span className="text-white font-semibold text-sm truncate max-w-[200px]">{project?.name || 'Untitled'}</span>
        <span className="text-[9px] font-mono px-2 py-0.5 rounded-full"
          style={{ background: 'rgba(14,165,233,0.1)', color: '#0EA5E9', border: '1px solid rgba(14,165,233,0.25)' }}>
          CANVAS
        </span>
        
        <div className="flex-1" />

        {/* Progress bar in top bar */}
        <div className="flex items-center gap-2">
          <div className="w-24 h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.05)' }}>
            <div className="h-full rounded-full transition-all duration-500"
              style={{ width: `${totalNodes > 0 ? (completedNodes / totalNodes) * 100 : 0}%`, background: 'linear-gradient(90deg, #0EA5E9, #10B981)' }} />
          </div>
          <span className="text-[10px] font-mono" style={{ color: '#64748B' }}>{completedNodes}/{totalNodes}</span>
        </div>

        <div className="w-px h-5" style={{ background: 'rgba(14,165,233,0.15)' }} />
        
        <div className="flex items-center gap-3 text-[10px] font-mono" style={{ color: '#64748B' }}>
          <span>Stage {project?.current_stage || 0}/17</span>
          <span>{shots.length} shots</span>
        </div>
        
        <button onClick={loadData} className="p-1.5 rounded-lg transition-colors"
          style={{ background: 'rgba(255,255,255,0.05)' }}
          title="Refresh"
          data-testid="canvas-refresh-btn">
          <RefreshCw className="w-3.5 h-3.5" style={{ color: '#64748B' }} />
        </button>
      </div>

      {/* Canvas + Panel */}
      <div className="flex-1 relative" data-testid="canvas-flow">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeClick={onNodeClick}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.35 }}
          minZoom={0.2}
          maxZoom={2}
          defaultEdgeOptions={{ type: 'smoothstep' }}
          proOptions={{ hideAttribution: true }}
          onPaneClick={() => setSelectedNode(null)}
        >
          <Background color="#1E293B" gap={20} size={1} />
          <Controls showInteractive={false} />
          <MiniMap
            nodeColor={(n) => {
              const s = n.data?.status;
              if (s === 'complete') return '#10B981';
              if (s === 'running') return '#0EA5E9';
              if (s === 'error') return '#EF4444';
              return '#1E293B';
            }}
            maskColor="rgba(11, 15, 25, 0.85)"
            style={{ background: '#0F172A', border: '1px solid rgba(14,165,233,0.15)', borderRadius: 8 }}
          />
        </ReactFlow>

        {/* Side Panel */}
        {selectedNode && (
          <NodePanel
            node={selectedNode}
            project={project}
            shots={shots}
            onClose={() => setSelectedNode(null)}
            onRun={onRunNode}
          />
        )}
      </div>
    </div>
  );
}
